import { TextComponent } from '@manifest-cyber/design-system/lib/components/TextComponent';
import {
  Alert,
  Collapse,
  Flex,
  Group,
  MultiSelectValueProps,
  Radio,
  Stack,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChipComponent from '../../../components/ChipComponent/ChipComponent';
import SelectOption from '../../../components/Form/SelectOption/SelectOption';
import Icon from '../../../components/Icon';
import ManifestModal, {
  ManifestModalProps,
} from '../../../components/ManifestModal/ManifestModal';
import { ManifestMultiselect } from '../../../components/ManifestMultiselect/ManifestMultiselect';
import { ManifestTextInput } from '../../../components/ManifestTextInput/ManifestTextInput';
import { RequiredAsterisk } from '../../../components/RequiredAsterisk/RequiredAsterisk';
import { TooltipInfoIcon } from '../../../components/TooltipInfoIcon/TooltipInfoIcon';
import {
  MappedOrganizationMember,
  OrganizationMemberRole,
} from '../../../hooks/queries/useFetchOrganizationUsers';
import { useAuth } from '../../../hooks/useAuth';
import { useOrganizationId } from '../../../hooks/utils/useOrganizationId';
import { filterUniqueElements } from '../../../lib/array/filterUniqueElements/filterUniqueElements';
import { MappedOrganization } from '../../../types/organization.interface';
import { useFetchRoles } from '../Roles/api/useFetchRoles';

interface MemberModalProps {
  onClose: () => void;
  onConfirm: (user: MemberFormValues) => Promise<void>;
  opened: boolean;
  isLoading?: boolean;
  org?: MappedOrganization;
  mode: 'create' | 'edit';
  defaultValues?: Omit<MemberFormValues, 'addAsAdminToAllSubOrgs'>;
  title: string;
  user?: MappedOrganizationMember;
  subOrganizationIds: string[];
}

export interface MemberFormValues {
  firstName: string;
  lastName: string;
  title?: string;
  email: string;
  roles: OrganizationMemberRole[];
  addAsAdminToAllSubOrgs: 'yes' | 'no';
}

export const MemberModal = ({
  onClose,
  onConfirm,
  isLoading,
  org: organization,
  defaultValues,
  mode,
  title,
  user,
  subOrganizationIds,
}: MemberModalProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<
    'form' | 'external' | 'confirmAddAsAdminToSubOrgs' | 'confirmRemoveAdminRole'
  >('form');
  const [isExternal, setIsExternal] = useState(false);
  const [organizationId] = useOrganizationId();
  const { data } = useFetchRoles({
    organizationId: organizationId ?? '',
  });
  const { user: currentUser } = useAuth();
  const isSelectedUserItself = user?._id === currentUser?.id;

  const roles = data?.items || [];
  const form = useForm<MemberFormValues>({
    initialValues:
      mode === 'edit' && defaultValues
        ? {
            ...defaultValues,
            addAsAdminToAllSubOrgs: 'yes',
          }
        : {
            firstName: '',
            lastName: '',
            title: '',
            email: '',
            roles: [],
            addAsAdminToAllSubOrgs: 'yes',
          },
    validateInputOnBlur: true,
    validate: {
      firstName: (value) =>
        value
          ? null
          : t('global.form.errors.requiredFieldWithName', {
              field: t('page.userSettings.organization.people.table.headers.firstName'),
            }),
      lastName: (value) =>
        value
          ? null
          : t('global.form.errors.requiredFieldWithName', {
              field: t('page.userSettings.organization.people.table.headers.lastName'),
            }),
      email: (value) => {
        if (!value) {
          return t('global.form.errors.requiredFieldWithName', {
            field: t('page.userSettings.organization.people.table.headers.email'),
          });
        }

        const hasAtAndDot = value.includes('@') && value.includes('.');
        return hasAtAndDot && validateEmail(value)
          ? null
          : t('global.form.errors.invalidEmailFormat');
      },
      roles: (value) => {
        if (value.length === 0) {
          return t('global.form.errors.mustSelectAtLeastOneOption');
        }

        return null;
      },
    },
  });

  useEffect(() => {
    if (organization?.defaultRole && mode === 'create') {
      const currentRoles = form.values.roles;
      const updatedRoles = filterUniqueElements(
        [
          {
            id: organization.defaultRole.id,
            name: organization.defaultRole.name,
            urn: organization.defaultRole.urn,
          },
          ...currentRoles,
        ],
        (role) => role.id,
      );

      form.setFieldValue('roles', Array.from(updatedRoles));
    }
  }, [organization?.defaultRole, mode]);

  const onCreateOrUpdate = () => {
    const isSelectedAdminRole = form.values.roles.some(
      (selectedRole) => selectedRole.urn === 'admin',
    );

    const hasSubOrgs = subOrganizationIds.length > 0;
    const isAdmin = Boolean(user?.roles?.some((role) => role.urn === 'admin'));
    const isAdminOfSubOrgs = Boolean(user?.adminOfSubOrgsIds?.length);
    const isAdminOfAllExistingSubOrgs =
      isAdminOfSubOrgs &&
      subOrganizationIds.length > 0 &&
      subOrganizationIds.every((subOrgId) => user?.adminOfSubOrgsIds?.includes(subOrgId));

    if (!form.isValid()) {
      form.validate();
      return;
    }
    if (isExternal) {
      setStep('external');
      return;
    }

    if (!isSelectedAdminRole && isAdmin && isAdminOfSubOrgs) {
      setStep('confirmRemoveAdminRole');
      return;
    }
    if (isSelectedAdminRole && hasSubOrgs && !isAdminOfAllExistingSubOrgs) {
      setStep('confirmAddAsAdminToSubOrgs');
      return;
    }
    onConfirm(form.values);
  };

  const onClickConfirmCreateExternalUser = () => {
    const isAdminMember = form.values.roles.some(
      (selectedRole) => selectedRole.urn === 'admin',
    );

    if (isAdminMember && subOrganizationIds.length > 0) {
      setStep('confirmAddAsAdminToSubOrgs');
      return;
    }

    onConfirm(form.values);
  };

  const externalModal: Omit<ManifestModalProps, 'opened' | 'onClose'> = {
    dataTestId: 'member-external-modal',
    title: t('page.userSettings.organization.people.table.headers.add-external-warn'),
    children: (
      <Stack>
        <TextComponent>
          {t('page.userSettings.organization.people.table.add-external-warn', {
            email: form.values.email,
          })}
        </TextComponent>
        <TextComponent>
          <b>{t('page.userSettings.organization.people.table.warn-are-you-sure', {})}</b>
        </TextComponent>
      </Stack>
    ),
    primaryAction: {
      label: t('page.userSettings.organization.people.table.confirm-warn'),
      onClick: onClickConfirmCreateExternalUser,
      isLoading,
    },
    secondaryAction: {
      label: t('global.cancel'),
      onClick: onClose,
    },
  };

  const adminModal: Omit<ManifestModalProps, 'opened' | 'onClose'> = {
    dataTestId: 'member-admin-modal',
    title: t('page.userSettings.organization.people.confirmAdminToAllSubOrgsModal.title'),
    children: (
      <>
        <Radio.Group
          defaultValue={form.values.addAsAdminToAllSubOrgs}
          value={form.values.addAsAdminToAllSubOrgs}
          onChange={(value) =>
            form.setFieldValue('addAsAdminToAllSubOrgs', value as 'yes' | 'no')
          }
        >
          <Group mt="xs">
            <Radio
              value="yes"
              label={t(
                'page.userSettings.organization.people.confirmAdminToAllSubOrgsModal.yesAdd',
              )}
            />
            <Radio
              value="no"
              label={t(
                'page.userSettings.organization.people.confirmAdminToAllSubOrgsModal.noAdd',
              )}
            />
          </Group>
        </Radio.Group>
      </>
    ),
    primaryAction: {
      label: t('global.confirm'),
      onClick: () => onConfirm(form.values),
      isLoading,
    },
    secondaryAction: {
      label: t('global.cancel'),
      onClick: onClose,
    },
  };

  const removeAdminRoleModal: Omit<ManifestModalProps, 'opened' | 'onClose'> = {
    dataTestId: 'member-remove-admin-role-modal',
    title: t('page.userSettings.organization.people.confirmRemoveAdminRoleModal.title'),
    children: t('page.userSettings.organization.people.confirmRemoveAdminRoleModal.body'),
    primaryAction: {
      label: t(
        'page.userSettings.organization.people.confirmRemoveAdminRoleModal.yesRemove',
      ),
      onClick: () => onConfirm(form.values),
      isLoading,
      color: 'red',
    },
    secondaryAction: {
      label: t('global.no'),
      onClick: onClose,
    },
  };

  const formModal: Omit<ManifestModalProps, 'opened' | 'onClose'> = {
    dataTestId: mode === 'create' ? 'member-create-modal' : 'member-edit-modal',
    title: title,
    children: (
      <Stack>
        <Collapse in={isExternal}>
          <Alert icon={<Icon icon="circle-info" />} color="yellow">
            {t('page.userSettings.organization.people.table.headers.invitingExternal')}
          </Alert>
        </Collapse>
        <Flex justify="space-between">
          <ManifestTextInput
            label={
              <>
                {t('page.userSettings.organization.people.table.headers.firstName')}
                <RequiredAsterisk />
              </>
            }
            style={{ width: '48%' }}
            error={form.errors.firstName}
            {...form.getInputProps('firstName')}
          />
          <ManifestTextInput
            label={
              <>
                {t('page.userSettings.organization.people.table.headers.lastName')}
                <RequiredAsterisk />
              </>
            }
            style={{ width: '48%' }}
            error={form.errors.lastName}
            {...form.getInputProps('lastName')}
          />
        </Flex>
        <ManifestTextInput
          label={
            <>
              {t('page.userSettings.organization.people.table.headers.email')}
              <RequiredAsterisk />
            </>
          }
          type="email"
          disabled={mode === 'edit'}
          {...form.getInputProps('email')}
          error={form.errors.email}
          onBlur={(e) => {
            const email = e.currentTarget.value;
            const emailDomain = email.includes('@') ? email.split('@')[1] : '';

            const isExternal = Boolean(
              validateEmail(email) &&
                emailDomain &&
                !organization?.domains?.includes(emailDomain),
            );

            setIsExternal(isExternal);
            form.getInputProps('email').onBlur(e);
          }}
        />
        <ManifestTextInput
          label={t('page.userSettings.organization.people.table.headers.title')}
          {...form.getInputProps('title')}
        />

        <ManifestMultiselect
          label={
            <>
              {t('page.userSettings.organization.people.memberModal.role')}
              <RequiredAsterisk />
              <TooltipInfoIcon
                ml="8px"
                maw={200}
                multiline
                label={t('page.userSettings.organization.people.memberModal.roleTooltip')}
              />
            </>
          }
          searchable
          itemComponent={SelectOption}
          valueComponent={({
            label,
            color,
            onRemove,
            className,
          }: MultiSelectValueProps) => {
            const currentChipRole = roles?.find((role) => role.name === label);
            const isCurrentChipAnAdminRole = currentChipRole?.urn === 'admin';
            const userHasAdminRole = user?.roles?.some((role) => role.urn === 'admin');
            const isAdminChipForCurrentUser =
              isCurrentChipAnAdminRole && isSelectedUserItself;
            const isAdminChipForCurrentAdminUser =
              isAdminChipForCurrentUser && userHasAdminRole;

            return (
              <ChipComponent
                showRemoveButton={
                  form.values.roles.length > 1 && !isAdminChipForCurrentAdminUser
                }
                className={className}
                leftIcon={{ isVisible: true, color: color }}
                label={label}
                onClick={onRemove}
              />
            );
          }}
          data={
            roles?.map((role) => ({
              label: role.name,
              value: role.id,
              color: role.color,
            })) || []
          }
          {...form.getInputProps('roles')}
          value={form.values.roles.map((role) => role.id)}
          defaultValue={form.values.roles.map((role) => role.id)}
          onChange={(selectedIds) => {
            let updatedRoleSelectionIds = [...selectedIds];
            const adminRoleDefinition = roles?.find((role) => role.urn === 'admin');
            const currentUserIsAdmin = user?.roles?.some((role) => role.urn === 'admin');
            const isAdminRoleCurrentlySelected =
              adminRoleDefinition && selectedIds.includes(adminRoleDefinition.id);

            const isAdminUserRemovingItsOwnAdminRole =
              isSelectedUserItself && currentUserIsAdmin && !isAdminRoleCurrentlySelected;

            if (isAdminUserRemovingItsOwnAdminRole && adminRoleDefinition) {
              updatedRoleSelectionIds.push(adminRoleDefinition.id);
            }

            const selectedRoleObjects = updatedRoleSelectionIds
              .map((id) => roles?.find((role) => role.id === id))
              .filter(Boolean) as OrganizationMemberRole[];
            form.setFieldValue('roles', selectedRoleObjects);
          }}
        />
      </Stack>
    ),
    primaryAction: {
      label:
        mode === 'create'
          ? t('page.userSettings.organization.people.table.confirm')
          : t('page.userSettings.organization.people.table.headers.edit-user-confirm'),

      onClick: onCreateOrUpdate,
      isLoading,
    },
    secondaryAction: {
      label: t('global.cancel'),
      onClick: onClose,
    },
  };

  const getModal = (): Omit<ManifestModalProps, 'opened' | 'onClose'> => {
    switch (step) {
      case 'form':
        return formModal;
      case 'external':
        return externalModal;
      case 'confirmAddAsAdminToSubOrgs':
        return adminModal;
      case 'confirmRemoveAdminRole':
        return removeAdminRoleModal;
      default:
        return formModal;
    }
  };

  const modal = getModal();
  return (
    <ManifestModal
      opened
      onClose={onClose}
      dataTestId={modal.dataTestId}
      title={modal.title}
      primaryAction={modal.primaryAction}
      secondaryAction={modal.secondaryAction}
    >
      {modal.children}
    </ManifestModal>
  );
};

const validateEmail = (email: string) =>
  !!email.length &&
  email
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
